"use client";

import { useMessageOrder } from "../../hooks/useMessageOrder";
import { useChatboxStore } from "../../store";
import { MessageContent } from "../MessageContent";
import { MessageFooter } from "../MessageFooter";
import { UiMessageBubble } from "../ui/messageBubble/UiMessageBubble";
import { MessagingStatus, type MessagingMessage } from "@/model/messaging";
import { isMessageOtherRead } from "@/modules/conversation/ConversationChatStore/utils/isRead";
import { useCallbackRef } from "@radix-ui/react-use-callback-ref";
import { type ComponentProps, useMemo } from "react";

export type MessageBubbleProps = {
  message: MessagingMessage;
  containerRef?: React.RefObject<HTMLDivElement>;
} & Pick<ComponentProps<"div">, "className" | "style">;

export function MessageBubble(props: MessageBubbleProps) {
  const { message, containerRef, ...restProps } = props;
  const {
    use: { options, unmarkedMessages },
    bind: { pushUnmarkedMessage, debouncedSetMessageStatus, resendMessage },
  } = useChatboxStore();

  const { maxWidth, bubble: bubbleOptions = {} } = options ?? {};
  const { styles } = bubbleOptions;

  const { order, showTimeBar } = useMessageOrder(message);

  // show time bar
  const timeBarVisible =
    message.status === MessagingStatus.Sending ? false : showTimeBar;

  // position
  const { position } = bubbleOptions;
  const finalPosition = useMemo(() => {
    return typeof position === "function"
      ? position(message)
      : (position ?? "received");
  }, [position]);

  // show avatar
  const { showAvatar } = bubbleOptions;
  const avatarVisible = useMemo(() => {
    if (typeof showAvatar === "function") {
      return showAvatar(message);
    }

    return showAvatar;
  }, [showAvatar, message]);

  // show name
  const { showName } = bubbleOptions;
  const nameVisible = useMemo(() => {
    if (typeof showName === "function") {
      return showName(message);
    }

    return showName;
  }, [showName, message]);

  // show read and delivered status
  const {
    payload: { deliveredStatus },
  } = message;
  const {
    participants,
    conversation: { participants: conversationParticipants } = {},
  } = options ?? {};
  const { isRead, isDelivered } = useMemo(() => {
    const myselfExternalId = participants?.myself?.externalId;

    return {
      isRead: isMessageOtherRead({
        deliveredStatus,
        myselfExternalId,
        participants: conversationParticipants,
      }),
      isDelivered: conversationParticipants?.some(
        (item) =>
          deliveredStatus?.[item.externalId!] === MessagingStatus.Delivered
      ),
    };
  }, [conversationParticipants, deliveredStatus]);

  // showDeliveredStatus
  const myselfExternalId = participants?.myself?.externalId;
  const messageParticipantExternalId = message.participant?.externalId;
  const showDeliveredStatus = messageParticipantExternalId === myselfExternalId;

  // handle intersect
  const handleIntersect = useCallbackRef(() => {
    const myselfID = options?.participants?.myself?.externalId;
    const markAsRead = options?.markAsRead;
    if (
      markAsRead &&
      !unmarkedMessages.has(message.uid) &&
      myselfID &&
      message.participant.externalId !==
        options?.participants?.myself?.externalId &&
      message.payload.deliveredStatus?.[myselfID] !== MessagingStatus.Read
    ) {
      pushUnmarkedMessage(message);
      debouncedSetMessageStatus();
    }
  });

  return (
    <UiMessageBubble
      {...restProps}
      message={message}
      position={finalPosition}
      order={order}
      timeBarVisible={timeBarVisible}
      containerElement={containerRef?.current}
      participantName={message.participant.name}
      avatar={message.participant.avatar}
      avatarVisible={avatarVisible}
      nameVisible={nameVisible}
      bubbleStyles={styles}
      isDelivered={isDelivered}
      isRead={isRead}
      maxWidth={maxWidth}
      deliveredStatusVisible={showDeliveredStatus}
      footer={<MessageFooter message={message} />}
      onResendMessage={() => {
        void resendMessage?.(message.uid);
      }}
      onIntersect={handleIntersect}
    >
      <MessageContent message={message} />
    </UiMessageBubble>
  );
}
